import React from 'react';
import { Link } from 'react-router-dom';
import { Target, ShieldWarning } from '@phosphor-icons/react';
import { formatINR } from '../../utils/formatters';

const SETUP_COLORS = {
  breakout: '#00FF66',
  momentum: '#2563EB',
  reversal: '#A855F7',
  'gap-fill': '#FBBF24',
  'volume-momentum': '#06B6D4',
};

const confidenceColor = (c) => {
  if (c >= 80) return '#00FF66';
  if (c >= 65) return '#FBBF24';
  return '#FF3366';
};

const upsideColor = (u) => {
  if (u >= 10) return '#00FF66';
  if (u >= 5) return '#FBBF24';
  return '#94A3B8';
};

export default function PickCard({ pick }) {
  const setupColor = SETUP_COLORS[pick.setup] || '#94A3B8';
  const confColor = confidenceColor(pick.confidence);

  return (
    <Link
      to={`/stock/${pick.symbol}`}
      className="bg-[#0A0B0E] border border-white/10 p-4 hover:border-[#A855F7]/50 transition-colors flex flex-col"
      data-testid={`pick-${pick.symbol}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="font-mono font-bold text-white">{pick.symbol}</div>
          <div className="text-xs text-[#64748B] truncate max-w-[140px]">{pick.name}</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-heading font-black" style={{ color: upsideColor(pick.upside_percent) }}>
            +{pick.upside_percent}%
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#64748B]">upside</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <span
          className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border"
          style={{ color: setupColor, borderColor: `${setupColor}40` }}
        >
          {pick.setup}
        </span>
        <span className="text-[10px] font-mono text-[#64748B]">{pick.sector}</span>
      </div>

      <div className="space-y-1 mb-3 text-xs font-mono">
        <Row label="Entry (LTP)" value={formatINR(pick.current_price)} valueColor="text-white" />
        <Row
          label={<><Target size={11} weight="bold" /> Target</>}
          value={formatINR(pick.target_price)}
          valueColor="text-[#00FF66]"
        />
        <Row
          label={<><ShieldWarning size={11} weight="bold" /> Stop Loss</>}
          value={formatINR(pick.stop_loss)}
          valueColor="text-[#FF3366]"
        />
        <Row label="R:R" value={`1:${pick.risk_reward}`} valueColor="text-white" />
      </div>

      <div className="pt-2 border-t border-white/5 mt-auto">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#64748B]">Confidence</span>
          <span className="text-xs font-mono font-bold" style={{ color: confColor }}>{pick.confidence}%</span>
        </div>
        <div className="h-1 bg-white/[0.05] relative">
          <div className="absolute h-full transition-all" style={{ width: `${pick.confidence}%`, background: confColor }} />
        </div>
        <p className="text-[10px] text-[#94A3B8] mt-2 italic leading-tight line-clamp-2">
          "{pick.rationale}"
        </p>
      </div>
    </Link>
  );
}

function Row({ label, value, valueColor }) {
  return (
    <div className="flex justify-between">
      <span className="text-[#64748B] flex items-center gap-1">{label}</span>
      <span className={valueColor}>{value}</span>
    </div>
  );
}
